// src/modules/nlp/LangModelLab.jsx
// Language Model Lab — N-gram · MLE · Smoothing · Generation · Perplexity · Interpolation · Neural LMs
// Tabs: input | ngram | generate | perplexity | smoothing | interpolation | neural | concepts

import React, { useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend, AreaChart, Area,
} from 'recharts';
import {
  tokenizeWords, tokenizeSentences, buildNgramLM, generateText,
  computePerplexity, SAMPLE_TEXTS,
} from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = 'var(--red)';
const P = ['var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)','var(--orange)','#f472b6','#34d399'];
const TABS = ['input','ngram','generate','perplexity','smoothing','interpolation','neural','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const MC = ({label,value,sub,color=ACCENT})=>(
  <div style={{background:'var(--raised)',border:`1px solid ${color}22`,borderRadius:'3px',padding:'12px 14px'}}>
    <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>
    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color}}>{value}</div>
    {sub&&<div style={{fontSize:'10px',color:'var(--t3)',marginTop:'2px'}}>{sub}</div>}
  </div>
);

function getContextDist(model, ctx, topK=14) {
  const dist = model.model[ctx];
  if (!dist) return [];
  return Object.entries(dist).sort((a,b)=>b[1]-a[1]).slice(0,topK)
    .map(([word,prob])=>({ word, prob:+prob.toFixed(4), pct:+(prob*100).toFixed(1) }));
}

function distEntropy(dist) {
  return +(-dist.reduce((s,d)=>d.prob>0?s+d.prob*Math.log2(d.prob):s, 0)).toFixed(3);
}

function sampleWithTemp(entries, temp) {
  if (!entries.length) return null;
  const logits = entries.map(([,p])=>Math.log(p+1e-10)/temp);
  const maxL   = Math.max(...logits);
  const exps   = logits.map(l=>Math.exp(l-maxL));
  const sum    = exps.reduce((a,b)=>a+b,0);
  const probs  = exps.map(e=>e/sum);
  let r=Math.random(), cum=0;
  for(let i=0;i<entries.length;i++){cum+=probs[i];if(r<cum)return entries[i][0];}
  return entries[entries.length-1][0];
}

export default function LangModelLab() {
  const [tab, setTab]     = useState('input');
  const [text, setText]   = useState(SAMPLE_TEXTS.tech + ' ' + SAMPLE_TEXTS.science);
  const [lmN, setLmN]     = useState(2);
  const [model, setModel] = useState(null);
  const [ctxInput, setCtxInput] = useState('');
  const [ctxDist,  setCtxDist]  = useState([]);

  // Generation
  const [seedText,    setSeedText]    = useState('');
  const [genLength,   setGenLength]   = useState(30);
  const [temperature, setTemperature] = useState(1.0);
  const [topK,        setTopK]        = useState(0);
  const [generated,   setGenerated]   = useState([]);

  // Perplexity
  const [testText,    setTestText]    = useState('');
  const [perpResult,  setPerpResult]  = useState(null);
  const [perpHistory, setPerpHistory] = useState([]);

  // Interpolation
  const [lambda1, setLambda1] = useState(0.6);
  const [lambda2, setLambda2] = useState(0.3);
  const [lambda3, setLambda3] = useState(0.1);

  const buildModel = useCallback(() => {
    const lm = buildNgramLM(text, lmN);
    setModel(lm);
    const firstCtx = tokenizeWords(text).slice(0, lmN-1).join(' ');
    setCtxInput(firstCtx);
    setCtxDist(getContextDist(lm, firstCtx));
    setGenerated([]);
    return lm;
  }, [text, lmN]);

  const lookupCtx = (lm, ctx) => setCtxDist(getContextDist(lm||model, ctx));

  const generate = (lm) => {
    const useLm = lm||model;
    if (!useLm) return;
    const seed = seedText.trim() ? tokenizeWords(seedText) : tokenizeWords(text).slice(0, useLm.n-1);
    const out  = [...seed];
    for (let i=0; i<genLength; i++) {
      const ctx  = out.slice(-(useLm.n-1)).join(' ');
      let   dist = Object.entries(useLm.model[ctx]||{});
      if (!dist.length) break;
      if (topK>0) dist = dist.sort((a,b)=>b[1]-a[1]).slice(0,topK);
      const word = sampleWithTemp(dist, temperature);
      if (!word) break;
      out.push(word);
    }
    setGenerated(out);
  };

  const runPerplexity = () => {
    if (!model || !testText.trim()) return;
    const pp      = computePerplexity(testText, model);
    const trainPP = computePerplexity(text.slice(0,200), model);
    setPerpResult({ test:pp, train:trainPP });
    setPerpHistory(h=>[...h.slice(-9), { label:testText.slice(0,18)+'…', pp }]);
  };

  const ctxSuggestions = model ? Object.keys(model.model).slice(0,10) : [];
  const lambdaSum      = +(lambda1+lambda2+lambda3).toFixed(2);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="TRAINING CORPUS" accent={ACCENT}>
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px',lineHeight:1.7}}>
              The LM learns P(word | context) from this corpus. Larger, domain-consistent corpora produce better models. Combine samples for richer distributions.
            </div>
            <textarea value={text} onChange={e=>{setText(e.target.value);setModel(null);}} style={{width:'100%',minHeight:'160px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>{setText(SAMPLE_TEXTS[key]);setModel(null);}} style={{padding:'5px 12px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=ACCENT} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b1)'}>{key}</button>
              ))}
              <button onClick={()=>{setText(Object.values(SAMPLE_TEXTS).join(' '));setModel(null);}} style={{padding:'5px 12px',border:`1px solid ${ACCENT}`,background:ACCENT+'18',color:ACCENT,fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>ALL SAMPLES</button>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {[
              ['Tokens',tokenizeWords(text).length,ACCENT],
              ['Vocab',new Set(tokenizeWords(text)).size,'var(--green)'],
              ['Sentences',tokenizeSentences(text).length,'var(--cyan)'],
              ['Avg freq',+(tokenizeWords(text).length/Math.max(1,new Set(tokenizeWords(text)).size)).toFixed(2),'var(--gold)'],
            ].map(([l,v,c])=>(
              <div key={l} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${c}22`,borderRadius:'3px',textAlign:'center'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>{String(l).toUpperCase()}</div>
                <div style={{fontSize:'20px',fontWeight:700,fontFamily:'Syne',color:c}}>{v}</div>
              </div>
            ))}
          </div>
          <InfoBox color={ACCENT} icon="◐">Use <strong>ALL SAMPLES</strong> for ~700 tokens — richer distributions. Higher N needs more data: trigram on 100-word corpus is too sparse. Stick to bigrams for small texts.</InfoBox>
        </div>
      )}

      {/* ── N-GRAM ── */}
      {tab==='ngram' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="BUILD N-GRAM LANGUAGE MODEL" accent={ACCENT}>
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              P(wₙ|w₁…wₙ₋₁) estimated by MLE: count(context+word)/count(context). Bigram uses 1 prev word, trigram uses 2. Higher N = more context but exponentially more sparse.
            </div>
            <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',marginBottom:'12px'}}>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>ORDER:</span>
              {[1,2,3,4].map(n=>(
                <button key={n} onClick={()=>setLmN(n)} style={{padding:'6px 16px',border:`1px solid ${lmN===n?ACCENT:'var(--b0)'}`,background:lmN===n?ACCENT+'18':'var(--raised)',color:lmN===n?ACCENT:'var(--t2)',fontSize:'11px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:lmN===n?700:400}}>
                  {['Unigram','Bigram','Trigram','4-gram'][n-1]}
                </button>
              ))}
              <Btn onClick={buildModel} color={ACCENT}>BUILD →</Btn>
            </div>

            {model && (
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'14px'}}>
                  <MC label="Vocab"      value={model.vocabSize}                        color={ACCENT}/>
                  <MC label="Contexts"   value={Object.keys(model.model).length}        color="var(--green)"/>
                  <MC label="Avg fan-out" value={+(Object.values(model.model).reduce((s,d)=>s+Object.keys(d).length,0)/Math.max(1,Object.keys(model.model).length)).toFixed(1)} sub="next words/ctx" color="var(--cyan)"/>
                  <MC label="Coverage"   value={+(Object.keys(model.model).length/Math.pow(model.vocabSize,Math.max(1,model.n-1))*100).toFixed(2)+'%'} sub="of possible contexts" color="var(--gold)"/>
                </div>

                <div style={{marginBottom:'12px'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.1em'}}>LOOKUP P(word | context)</div>
                  <div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
                    <input value={ctxInput} onChange={e=>{setCtxInput(e.target.value);lookupCtx(model,e.target.value.trim().toLowerCase());}} placeholder={lmN===1?'(unigram — no context)':'Enter context words…'} disabled={lmN===1} style={{flex:1,background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',opacity:lmN===1?.5:1}}/>
                  </div>
                  {lmN===1 && (() => {
                    const uniDist = Object.entries(model.model['']).sort((a,b)=>b[1]-a[1]).slice(0,14).map(([word,prob])=>({word,prob:+prob.toFixed(4),pct:+(prob*100).toFixed(1)}));
                    return (
                      <>
                        <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px'}}>Unigram P(word) — top 14 · entropy: {distEntropy(uniDist)} bits</div>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={uniDist}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                            <XAxis dataKey="word" stroke="var(--t3)" tick={{fontSize:8}} angle={-20} textAnchor="end" height={44} interval={0}/>
                            <YAxis stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`${(v*100).toFixed(0)}%`}/>
                            <Tooltip formatter={v=>[`${(v*100).toFixed(1)}%`]}/>
                            <Bar dataKey="prob" radius={[2,2,0,0]}>{uniDist.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </>
                    );
                  })()}
                  {lmN>1 && (
                    <div style={{display:'flex',gap:'5px',flexWrap:'wrap',marginBottom:'8px'}}>
                      {ctxSuggestions.slice(0,8).map(ctx=>(
                        <button key={ctx} onClick={()=>{setCtxInput(ctx);setCtxDist(getContextDist(model,ctx));}} style={{padding:'3px 8px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                          onMouseEnter={e=>e.currentTarget.style.color=ACCENT} onMouseLeave={e=>e.currentTarget.style.color='var(--t3)'}>{ctx}</button>
                      ))}
                    </div>
                  )}
                </div>

                {ctxDist.length > 0 && (
                  <>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>
                      P(w | "{ctxInput}") — {ctxDist.length} continuations · entropy: {distEntropy(ctxDist)} bits
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={ctxDist}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="word" stroke="var(--t3)" tick={{fontSize:8}} angle={-20} textAnchor="end" height={44} interval={0}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`${(v*100).toFixed(0)}%`} domain={[0,'auto']}/>
                        <Tooltip formatter={v=>[`${(v*100).toFixed(1)}%`,'probability']}/>
                        <Bar dataKey="prob" radius={[2,2,0,0]}>{ctxDist.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'5px',marginTop:'10px'}}>
                      {ctxDist.map(({word,pct},i)=>(
                        <div key={word} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 10px',background:'var(--raised)',borderRadius:'2px',border:'1px solid var(--b0)'}}>
                          <span style={{fontSize:'11px',fontFamily:'JetBrains Mono',color:'var(--t1)'}}><span style={{color:'var(--t3)',fontSize:'9px',marginRight:'4px'}}>#{i+1}</span>{word}</span>
                          <div style={{display:'flex',gap:'5px',alignItems:'center'}}>
                            <div style={{width:'36px',height:'4px',background:'var(--deep)',borderRadius:'2px'}}>
                              <div style={{height:'100%',width:`${pct}%`,background:P[i%P.length],borderRadius:'2px'}}/>
                            </div>
                            <span style={{fontSize:'10px',color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700,minWidth:'32px',textAlign:'right'}}>{pct}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {lmN>1&&ctxInput&&!ctxDist.length&&<div style={{padding:'16px',textAlign:'center',color:'var(--t3)',fontSize:'11px'}}>Context "{ctxInput}" not found. Try one of the suggestions above.</div>}
              </>
            )}
            {!model && <InfoBox color={ACCENT} icon="◐">Select N-gram order and click BUILD. Unigram = global word freq. Bigram = conditioned on previous word.</InfoBox>}
          </Panel>

          <Panel title="MLE FORMULA & THE ZERO-COUNT PROBLEM" accent={ACCENT}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
              <div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.75,marginBottom:'10px'}}>
                  <code style={{color:ACCENT,fontFamily:'JetBrains Mono',fontSize:'12px'}}>P(wₙ|wₙ₋₁) = C(wₙ₋₁,wₙ) / C(wₙ₋₁)</code>
                  <br/><br/>
                  Example — "the cat sat on the cat mat the cat"<br/>
                  P(cat|the) = 3/3 = <strong style={{color:'var(--green)'}}>1.0</strong><br/>
                  P(sat|cat) = 1/3 = <strong style={{color:'var(--cyan)'}}>0.333</strong><br/>
                  P(mat|cat) = 1/3 = <strong style={{color:'var(--cyan)'}}>0.333</strong><br/>
                  P(dog|the) = 0/3 = <strong style={{color:'var(--red)'}}>0 ← PROBLEM</strong>
                </div>
              </div>
              <div style={{padding:'12px',background:'var(--deep)',border:'1px solid var(--red)33',borderRadius:'4px'}}>
                <div style={{fontSize:'11px',fontWeight:700,color:'var(--red)',marginBottom:'8px'}}>The Sparsity Problem</div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.7}}>
                  Vocabulary V=10,000. Possible bigrams: V²=100M. Typical 1M-word corpus: ~1M observed bigrams → 99% unseen. Any sentence with a new bigram → P=0 → Perplexity=∞. Solution on next tab: smoothing.
                </div>
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── GENERATE ── */}
      {tab==='generate' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!model && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={buildModel} color={ACCENT}>BUILD MODEL FIRST →</Btn></div>}
          {model && (
            <>
              <Panel title="TEXT GENERATION — SAMPLING FROM THE MODEL" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',marginBottom:'14px'}}>
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>SEED TEXT (optional)</div>
                    <input value={seedText} onChange={e=>setSeedText(e.target.value)} placeholder="Leave empty for random start…" style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                    <div>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px',letterSpacing:'.1em'}}>LENGTH: {genLength} tokens</div>
                      <input type="range" min={5} max={80} value={genLength} onChange={e=>setGenLength(+e.target.value)} style={{width:'100%',accentColor:ACCENT}}/>
                    </div>
                    <div>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px',letterSpacing:'.1em'}}>TEMPERATURE: {temperature} {temperature<0.5?'— greedy':temperature>1.5?'— creative':'— balanced'}</div>
                      <input type="range" min={0.1} max={2.5} step={0.1} value={temperature} onChange={e=>setTemperature(+e.target.value)} style={{width:'100%',accentColor:'var(--orange)'}}/>
                    </div>
                    <div>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px',letterSpacing:'.1em'}}>TOP-K: {topK===0?'OFF (full dist)':topK}</div>
                      <input type="range" min={0} max={20} value={topK} onChange={e=>setTopK(+e.target.value)} style={{width:'100%',accentColor:'var(--violet)'}}/>
                    </div>
                  </div>
                </div>
                <Btn onClick={()=>generate()} color={ACCENT}>GENERATE →</Btn>

                {generated.length > 0 && (
                  <div style={{marginTop:'14px'}}>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>GENERATED OUTPUT ({generated.length} tokens)</div>
                    <div style={{padding:'14px',background:'var(--deep)',border:'1px solid var(--red)33',borderRadius:'3px',lineHeight:2.2,fontSize:'13px',fontFamily:'JetBrains Mono'}}>
                      {generated.map((word,i)=>{
                        const isSeed = i < (seedText.trim()?tokenizeWords(seedText).length:lmN-1);
                        return <span key={i} style={{color:isSeed?'var(--cyan)':ACCENT,fontWeight:isSeed?700:400,margin:'0 2px',padding:'1px 4px',borderRadius:'2px',background:isSeed?'var(--gcyan)':'transparent'}}>{word}</span>;
                      })}
                    </div>
                    <div style={{display:'flex',gap:'8px',marginTop:'8px'}}>
                      <Tag color="var(--cyan)">seed</Tag><Tag color={ACCENT}>generated</Tag>
                    </div>
                  </div>
                )}
              </Panel>

              {/* Temperature chart */}
              {ctxDist.length > 0 && (
                <Panel title="TEMPERATURE EFFECT ON NEXT-WORD DISTRIBUTION" accent="var(--orange)">
                  <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px'}}>T→0: greedy (peaks sharpen). T=1: original. T→∞: uniform random.</div>
                  {(()=>{
                    const baseProbs = ctxDist.map(d=>d.prob);
                    const applyTemp = (T) => {
                      const logits = baseProbs.map(p=>Math.log(p+1e-10)/T);
                      const maxL   = Math.max(...logits);
                      const exps   = logits.map(l=>Math.exp(l-maxL));
                      const sum    = exps.reduce((a,b)=>a+b,0);
                      return exps.map(e=>+(e/sum*100).toFixed(1));
                    };
                    const data = ctxDist.slice(0,8).map((d,i)=>({
                      word:d.word, t01:applyTemp(0.1)[i], t05:applyTemp(0.5)[i],
                      t10:d.pct, t20:applyTemp(2.0)[i],
                    }));
                    return (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                          <XAxis dataKey="word" stroke="var(--t3)" tick={{fontSize:9}} angle={-15} textAnchor="end" height={44} interval={0}/>
                          <YAxis stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`${v}%`}/>
                          <Tooltip formatter={v=>[`${v}%`]}/>
                          <Legend wrapperStyle={{fontSize:'9px'}}/>
                          <Bar dataKey="t01" fill="var(--red)"    opacity={.85} name="T=0.1"/>
                          <Bar dataKey="t05" fill="var(--orange)" opacity={.85} name="T=0.5"/>
                          <Bar dataKey="t10" fill="var(--gold)"   opacity={.85} name="T=1.0"/>
                          <Bar dataKey="t20" fill="var(--cyan)"   opacity={.85} name="T=2.0"/>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </Panel>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PERPLEXITY ── */}
      {tab==='perplexity' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!model && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={buildModel} color={ACCENT}>BUILD MODEL FIRST →</Btn></div>}
          {model && (
            <>
              <Panel title="PERPLEXITY — MEASURING MODEL QUALITY" accent="var(--violet)">
                <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.75}}>
                  PP(W) = 2^(−(1/N)Σlog₂P(wᵢ|ctx)). Lower = model predicts text better. Perplexity K means: as confused as choosing uniformly among K words at each step.
                </div>
                <div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
                  <input value={testText} onChange={e=>setTestText(e.target.value)} placeholder="Paste test text to evaluate…" style={{flex:1,background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}/>
                  <Btn onClick={runPerplexity} color="var(--violet)">COMPUTE →</Btn>
                </div>
                <div style={{display:'flex',gap:'5px',flexWrap:'wrap',marginBottom:'12px'}}>
                  {[
                    ['In-domain', text.slice(50,180)],
                    ['Science', 'Quantum computing enables exponential speedup for specific computational problems.'],
                    ['Random', 'purple elephant dancing silently above banana refrigerator tomorrow'],
                    ['Simple', 'The cat sat on the mat. The dog ran fast in the park.'],
                  ].map(([l,ex])=>(
                    <button key={l} onClick={()=>setTestText(ex)} style={{padding:'4px 10px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                      onMouseEnter={e=>e.currentTarget.style.color='var(--violet)'} onMouseLeave={e=>e.currentTarget.style.color='var(--t3)'}>{l}</button>
                  ))}
                </div>

                {perpResult && (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                    {[
                      {label:'TEST PERPLEXITY',val:perpResult.test,sub:perpResult.test>2000?'Very different domain':perpResult.test>500?'Different domain':perpResult.test>100?'Some mismatch':perpResult.test>30?'Similar domain':'Same domain',color:perpResult.test>2000?'var(--red)':perpResult.test>500?'var(--orange)':perpResult.test>100?'var(--gold)':'var(--green)'},
                      {label:'TRAIN SAMPLE PP',val:perpResult.train,sub:'on 200-char training slice',color:'var(--green)'},
                    ].map(({label,val,sub,color})=>(
                      <div key={label} style={{padding:'20px',background:'var(--deep)',border:`1px solid ${color}44`,borderRadius:'4px',textAlign:'center'}}>
                        <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>{label}</div>
                        <div style={{fontSize:'36px',fontWeight:800,fontFamily:'Syne',color}}>{val}</div>
                        <div style={{fontSize:'11px',color:'var(--t3)',marginTop:'6px'}}>{sub}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {perpHistory.length > 1 && (
                <Panel title="PERPLEXITY COMPARISON" accent="var(--violet)">
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={perpHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="label" stroke="var(--t3)" tick={{fontSize:8}} angle={-15} textAnchor="end" height={44} interval={0}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="pp" name="Perplexity" radius={[2,2,0,0]}>
                        {perpHistory.map(({pp},i)=><Cell key={i} fill={pp>2000?'var(--red)':pp>500?'var(--orange)':pp>100?'var(--gold)':'var(--green)'} opacity={.85}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              )}

              <Panel title="PERPLEXITY REFERENCE" accent="var(--violet)">
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                  {[
                    {range:'< 30',quality:'Excellent',desc:'Same text / near-perfect prediction. GPT-2 on PTB: ~35.',color:'var(--green)'},
                    {range:'30–100',quality:'Good',desc:'Same domain, different sentences. Well-trained model.',color:'var(--cyan)'},
                    {range:'100–500',quality:'Moderate',desc:'Related domain. Noticeable vocab mismatch.',color:'var(--gold)'},
                    {range:'500–2K',quality:'Poor',desc:'Different domain. Heavy smoothing reliance.',color:'var(--orange)'},
                    {range:'2K–10K',quality:'Very Poor',desc:'Mostly random. Consider domain retraining.',color:'var(--red)'},
                    {range:'> 10K',quality:'Random',desc:'Uniform-like. Model learned nothing useful here.',color:'var(--red)'},
                  ].map(({range,quality,desc,color})=>(
                    <div key={range} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                      <div style={{fontSize:'12px',fontWeight:700,color,fontFamily:'JetBrains Mono'}}>{range}</div>
                      <div style={{fontSize:'10px',color,marginBottom:'4px'}}>{quality}</div>
                      <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── SMOOTHING ── */}
      {tab==='smoothing' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="SMOOTHING — HANDLING ZERO-COUNT N-GRAMS" accent="var(--green)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.7}}>
              MLE assigns P=0 to unseen N-grams → PP=∞. Smoothing redistributes mass from seen to unseen events. Essential for real-world LMs.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {name:'Add-1 / Laplace',color:'var(--cyan)',formula:'(C(ctx,w)+1)/(C(ctx)+V)',pro:'Simple. Always non-zero.',con:'Over-allocates mass to unseen. Poor for large V.',use:'Naive Bayes classification'},
                {name:'Add-k / Lidstone',color:'var(--green)',formula:'(C(ctx,w)+k)/(C(ctx)+k·V)',pro:'Tunable 0<k<1. Better than Add-1.',con:'k must be tuned. Still problematic at scale.',use:'Better baseline'},
                {name:'Good-Turing',color:'var(--gold)',formula:'c*(w) = (c+1)·N(c+1)/N(c)',pro:'Principled. Uses count-of-counts.',con:'Unstable for rare events.',use:'Classic; rarely used now'},
                {name:'Katz Backoff',color:'var(--violet)',formula:'P_KN if C>0 else α·P(w|shorter ctx)',pro:'Falls back to lower-order LM.',con:'Discount α complex. Suboptimal.',use:'Speech recognition 1990s'},
                {name:'Interpolation',color:'var(--orange)',formula:'λ₁P₃+λ₂P₂+λ₃P₁, Σλ=1',pro:'Smooth fallback across orders.',con:'Weights need EM estimation.',use:'ASR, MT, standard'},
                {name:'Kneser-Ney',color:'var(--red)',formula:'P_KN = max(C-d,0)/C(ctx) + β·P_cont',pro:'Continuation prob. State-of-art.',con:'Complex to implement correctly.',use:'SRILM/KenLM default'},
              ].map(({name,color,formula,pro,con,use})=>(
                <div key={name} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'6px'}}>{name}</div>
                  <div style={{padding:'5px 7px',background:'var(--deep)',borderRadius:'2px',fontFamily:'JetBrains Mono',fontSize:'9px',color:'var(--code)',marginBottom:'7px',wordBreak:'break-all'}}>{formula}</div>
                  <div style={{fontSize:'9px',color:'var(--green)',marginBottom:'2px'}}>✓ {pro}</div>
                  <div style={{fontSize:'9px',color:'var(--red)',marginBottom:'4px'}}>✗ {con}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>📦 {use}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="PROBABILITY MASS REDISTRIBUTION" accent="var(--green)">
            {(()=>{
              const V = model?.vocabSize || 150;
              const data=[
                {method:'MLE',seen:100,unseen:0},
                {method:'Add-0.01k',seen:+(100/(1+0.01*V)*100).toFixed(0),unseen:+(0.01*V/(1+0.01*V)*100).toFixed(0)},
                {method:'Add-1 (Laplace)',seen:+(100/(100+V)*100).toFixed(0),unseen:+(V/(100+V)*100).toFixed(0)},
                {method:'Interpolation',seen:85,unseen:15},
                {method:'Kneser-Ney',seen:88,unseen:12},
              ];
              return (
                <>
                  <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px'}}>How much probability mass shifts from seen events to the unseen pool (varies by smoothing strength).</div>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="method" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`${v}%`}/>
                      <Tooltip/>
                      <Legend wrapperStyle={{fontSize:'9px'}}/>
                      <Bar dataKey="seen"   fill="var(--cyan)"  opacity={.8} name="Seen events %" stackId="a"/>
                      <Bar dataKey="unseen" fill="var(--green)" opacity={.8} name="Unseen pool %" stackId="a"/>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              );
            })()}
          </Panel>
        </div>
      )}

      {/* ── INTERPOLATION ── */}
      {tab==='interpolation' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="LINEAR INTERPOLATION" accent="var(--orange)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.75}}>
              P̃(wₙ|wₙ₋₂,wₙ₋₁) = <strong style={{color:'var(--orange)'}}>λ₁</strong>·P₃ + <strong style={{color:'var(--gold)'}}>λ₂</strong>·P₂ + <strong style={{color:'var(--cyan)'}}>λ₃</strong>·P₁ where Σλ=1. Weights estimated on held-out data with EM. Higher-order gets more weight when data supports it.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginBottom:'12px'}}>
              {[
                {label:'λ₁ Trigram',val:lambda1,set:setLambda1,color:'var(--orange)'},
                {label:'λ₂ Bigram', val:lambda2,set:setLambda2,color:'var(--gold)'},
                {label:'λ₃ Unigram',val:lambda3,set:setLambda3,color:'var(--cyan)'},
              ].map(({label,val,set,color})=>(
                <div key={label} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'4px',textAlign:'center'}}>
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'6px'}}>{label}</div>
                  <input type="range" min={0} max={1} step={0.05} value={val} onChange={e=>set(+e.target.value)} style={{width:'100%',accentColor:color,marginBottom:'8px'}}/>
                  <div style={{fontSize:'22px',fontWeight:700,fontFamily:'Syne',color}}>{val.toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div style={{padding:'10px 14px',background:'var(--deep)',border:`1px solid ${Math.abs(lambdaSum-1)<0.01?'var(--green)':'var(--red)'}44`,borderRadius:'3px',display:'flex',gap:'12px',alignItems:'center'}}>
              <span style={{fontSize:'12px',fontFamily:'JetBrains Mono',color:'var(--t2)'}}>λ₁+λ₂+λ₃ = <strong style={{color:Math.abs(lambdaSum-1)<0.01?'var(--green)':'var(--red)'}}>{lambdaSum}</strong></span>
              {Math.abs(lambdaSum-1)>0.01&&<Tag color="var(--red)">must sum to 1.0</Tag>}
              {Math.abs(lambdaSum-1)<=0.01&&<Tag color="var(--green)">✓ valid</Tag>}
            </div>
          </Panel>

          {/* Weight visualization */}
          <Panel title="CONTRIBUTION OF EACH ORDER" accent="var(--orange)">
            {(()=>{
              const data=[
                {order:'Trigram P₃',λ:lambda1,color:'var(--orange)'},
                {order:'Bigram P₂', λ:lambda2,color:'var(--gold)'},
                {order:'Unigram P₁',λ:lambda3,color:'var(--cyan)'},
              ];
              return (
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  {data.map(({order,λ,color})=>(
                    <div key={order}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                        <span style={{fontSize:'11px',color:'var(--t2)'}}>{order}</span>
                        <span style={{fontSize:'12px',fontWeight:700,color,fontFamily:'JetBrains Mono'}}>{λ.toFixed(2)} ({(λ*100).toFixed(0)}%)</span>
                      </div>
                      <div style={{height:'14px',background:'var(--deep)',borderRadius:'3px',overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${λ*100}%`,background:color,borderRadius:'3px',transition:'width .3s'}}/>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <InfoBox color="var(--orange)" icon="◐" style={{marginTop:'10px'}}>
              Typical learned weights: trigram λ₁≈0.6, bigram λ₂≈0.3, unigram λ₃≈0.1. When trigram context is sparse, model automatically leans on bigram and unigram via interpolation — no explicit backoff decision needed.
            </InfoBox>
          </Panel>
        </div>
      )}

      {/* ── NEURAL ── */}
      {tab==='neural' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="NEURAL LANGUAGE MODELS — FROM N-GRAM TO GPT" accent="#f472b6">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              {[
                {title:'NNLM — Bengio 2003',color:'var(--cyan)',desc:'First neural LM. Embeds N-1 words in shared C-dim space, concatenates, feeds tanh hidden layer → softmax. Fixed context. Solved sparsity — any combo has non-zero prob. 17K vocab × 100d = 1.7M params. 10× smaller than N-gram tables.'},
                {title:'RNN-LM — Mikolov 2010',color:'var(--green)',desc:'hₜ=tanh(Wxₜ+Uhₜ₋₁). Theoretically infinite context. BPTT training. Vanishing gradients limit effective context to ~20 tokens. First model to outperform N-gram LMs on Penn Treebank. Foundation of seq2seq MT.'},
                {title:'LSTM-LM 2013+',color:'var(--violet)',desc:'Gates (input/forget/output) fix vanishing gradients. Cell state carries long-range info. 2-layer LSTM, 650 hidden = AWD-LSTM: PTB perplexity ~54. Much stronger than n-gram baseline. Standard until Transformers.'},
                {title:'GPT — Decoder Transformer 2018',color:'var(--gold)',desc:'Autoregressive: predict next from all previous. Causal (masked) self-attention — position j only attends to i≤j. Pre-trained on BooksCorpus+Wiki. GPT-1: 110M. GPT-2: 1.5B, zero-shot transfer. GPT-3: 175B, few-shot.'},
                {title:'BERT — Encoder 2018',color:'var(--orange)',desc:'MLM: predict 15% randomly masked tokens using full bidirectional context. Not autoregressive — cannot generate text left-to-right. Pre-trained on 3.3B words. 110M/340M. Still dominant for discriminative tasks (classification, NER, QA).'},
                {title:'Scale & Emergence 2020+',color:'#f472b6',desc:'Chinchilla law: train N-param model on ~20N tokens. GPT-4: ~1T params. RLHF aligns with human preferences. Chain-of-thought prompting enables complex reasoning. Emergent abilities appear above ~10B params threshold.'},
              ].map(({title,color,desc})=>(
                <div key={title} style={{padding:'14px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{color,fontWeight:700,fontSize:'12px',marginBottom:'8px'}}>{title}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="PERPLEXITY PROGRESS ON PENN TREEBANK" accent="#f472b6">
            {(()=>{
              const data=[
                {year:'2000',model:'Trigram KN',ppl:141},{year:'2003',model:'NNLM',ppl:109},
                {year:'2010',model:'RNN',ppl:124},{year:'2013',model:'LSTM',ppl:78},
                {year:'2016',model:'AWD-LSTM',ppl:54},{year:'2018',model:'Transformer',ppl:47},
                {year:'2019',model:'GPT-2 ft',ppl:22},{year:'2020',model:'GPT-3',ppl:20},
              ];
              return (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="year" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}} label={{value:'PTB PPL (↓=better)',angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}}/>
                    <Tooltip content={({active,payload})=>active&&payload?.length?(
                      <div style={{background:'var(--raised)',border:'1px solid var(--b1)',padding:'8px',borderRadius:'3px',fontSize:'10px'}}>
                        <div style={{color:'#f472b6',fontWeight:700}}>{payload[0].payload.model}</div>
                        <div style={{color:'var(--t3)'}}>PPL: {payload[0].payload.ppl}</div>
                      </div>
                    ):null}/>
                    <Area type="monotone" dataKey="ppl" stroke="#f472b6" fill="#f472b618" strokeWidth={2.5}/>
                  </AreaChart>
                </ResponsiveContainer>
              );
            })()}
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Markov Assumption',color:ACCENT,desc:'N-gram LMs assume: P(wₙ|w₁…wₙ₋₁) ≈ P(wₙ|wₙ₋N+1…wₙ₋₁). Only the last N-1 words matter. Reality: long-range dependencies ("The government plans to build a new nuclear ___") require large N or neural architectures. Neural LMs relax this assumption.'},
              {title:'Entropy & Cross-Entropy',color:'var(--violet)',desc:'Language entropy: H(L)≈−(1/N)Σlog₂P(wᵢ|ctx). English ≈ 1 bit/char (Shannon 1951). Cross-entropy H(p,q)=−Σp(x)log q(x). log₂(perplexity) = model cross-entropy in bits/word. PP=100 → 6.64 bits/word.'},
              {title:'Applications of LMs',color:'var(--green)',desc:'Speech recognition: P(words|audio)∝P(audio|words)×P(words). Machine translation: rank target hypotheses. Spell correction: rank candidates. Predictive text (SwiftKey, T9). Code completion (Copilot). All reduce to: score a sequence of tokens.'},
              {title:'Training Data & Scale',color:'var(--gold)',desc:'GPT-2: 40GB WebText. GPT-3: 570GB cleaned CommonCrawl+Books+Wiki. LLaMA-2: 2T tokens. Chinchilla (2022): optimal compute splits evenly between params and tokens. 1B param model optimally trained on 20B tokens. Quality > quantity.'},
              {title:'In-Context Learning',color:'var(--orange)',desc:'GPT-3 discovery: LLMs can perform new tasks via few-shot examples in the prompt — no gradient updates. "Q: 2+2=? A: 4. Q: 3+3=? A:" → "6". Emergent at ~10B params. Chain-of-thought: "Think step by step" dramatically improves multi-step reasoning.'},
              {title:'Tokenization Impact',color:'var(--cyan)',desc:'Character LM: small vocab, long sequences. Word LM: large vocab, OOV. Subword (GPT-4: 100K BPE). More tokens = more compute = higher cost. Non-English languages tokenize less efficiently: French/German may need 2-3× more tokens per idea than English.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
